import { Button, Divider, Spin } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import ContentLayoutManagement from "../../../../layouts/ContentLayoutManagement";
import HeaderContent from "../../../../layouts/HeaderContent";
import CommonComponents from "../../../../components/Commons";
import useToggle from "../../../../hooks/useToggle";

import
{
   getGeneralFormOrderArchiveAsync,
   getOrderByIdUserAsync,
   patchArchiveOrderAsync,
} from "../../../../store/reducers/orderSlice";
import { RootState } from "../../../../store/store";
import { ESttausCode } from "../../../../enums";
import { getErrorMsg } from "../../../../helpers/common";
import { showError, showSuccess } from "../../../../helpers/toast";

import UnifiedDynamicForm from "../../../../components/VMRS/ExploreTasking/components/form/UnifiedDynamicForm";
import CartImageOrder from "../../../../components/VMRS/Order/Tasking/CartImageOrder";
import { Section } from "../../../../components/VMRS/Order/DetailSection";
import ImageInfo from "../../../../components/VMRS/ExploreArchive/ImageInfo";
import ImagesMapVMRS from "../../../../components/VMRS/MapAdapter/ImagesMap";
import { flyToGeometries, showFootprint } from "../../../../components/VMRS/ExploreArchive/ultis";
import
{
   changeBasemap,
   setBasemapList,
} from "../../../../components/VMRS/MapAdapter/store/slices/mapSlice";
import { clearAllLayers } from "../../../../components/VMRS/MapAdapter/store/slices/layerSlice";
import { clearAOIFeatures, setDrawingAOIMode } from "../../../../components/VMRS/MapAdapter/store/slices/aoiSlice";
import { setDrawingMode } from "../../../../components/VMRS/MapAdapter/store/slices/drawSlice";
import { setMeasureMode } from "../../../../components/VMRS/MapAdapter/store/slices/measureSlice";
import { BASEMAPS } from "../../../../components/VMRS/MapAdapter/utils/consts";
import { loadBasemaps } from "../../../../components/VMRS/MapAdapter/store/thunks/loadBasemaps";

const transformGeneralSchema = (apiData: any) =>
{
   return [
      {
         step_id: "general_step",
         name: apiData?.name,
         ui: apiData?.ui_schema?.ui,
      },
   ];
};

// Map an order object onto the general form fields
const transformOrderToFormData = (order: any, steps: any[]) =>
{
   const fd: Record<string, any> = {};

   steps.forEach((step: any) =>
   {
      step.ui?.order?.forEach((field: string) =>
      {
         let value = order?.[field];

         // Aliases between order object and form field keys
         if (value === undefined && field === "order_name") value = order?.name;

         // Fallback to schema default
         if (value === undefined)
         {
            const cfg = step.ui?.options?.[field];
            if (cfg?.default !== undefined) value = cfg.default;
         }

         if (value !== undefined) fd[field] = value;
      });
   });

   return fd;
};

export default function EditArchiveOrder()
{
   const { t } = useTranslation();
   const { id } = useParams();
   const dispatch = useDispatch<any>();
   const navigate = useNavigate();
   const location = useLocation();

   const [loading, setLoading] = useState(false);
   const [loadingSchemas, setLoadingSchemas] = useState(true);
   const [data, setData] = useState<any>({});
   const [generalSteps, setGeneralSteps] = useState<any[]>([]);
   const [formData, setFormData] = useState<any>({});
   const [initialFormData, setInitialFormData] = useState<any>({});
   const [openProviders, setOpenProviders] = useState<Record<string, boolean>>({});
   const [engineMap, setEngineMap] = useState<maplibregl.Map | undefined>(undefined);
   const [selectedView, setSelectedView] = useState<any>(undefined);

   const {
      mapType,
      view: mapView,
      selectedBasemap,
   } = useSelector((state: RootState) => state.map);

   const {
      open: openConfirm,
      toggle: toggleConfirm,
      shouldRender: shouldRenderConfirm,
   } = useToggle();

   const {
      open: openViewInfo,
      toggle: toggleViewInfo,
      shouldRender: shouldRenderViewInfo,
   } = useToggle();

   const handleViewInfo = (item?: any) =>
   {
      setSelectedView(item);
      toggleViewInfo();
   };

   // ============== FETCH ORDER + SCHEMA ==============
   const getData = async () =>
   {
      try
      {
         setLoadingSchemas(true);

         const [orderRes, schemaRes] = await Promise.all([
            // @ts-ignore
            dispatch(getOrderByIdUserAsync(id)).unwrap(),
            // @ts-ignore
            dispatch(getGeneralFormOrderArchiveAsync()).unwrap(),
         ]);

         const order = orderRes?.data?.data ?? {};
         const steps = transformGeneralSchema(schemaRes?.data?.data);

         const initialForm = transformOrderToFormData(order, steps);
         setData(order);
         setGeneralSteps(steps);
         setFormData(initialForm);
         setInitialFormData(initialForm);

         // open all provider sections + draw footprints
         const init: Record<string, boolean> = {};
         const geometries: any[] = [];
         (order?.items || []).forEach((it: any) =>
         {
            init[it?.provider_code] = true;
            (it?.stac_items || []).forEach((s: any) =>
            {
               if (s?.geometry)
               {
                  showFootprint(
                     { ...s, provider_code: s?.provider_code ?? it?.provider_code },
                     dispatch,
                     engineMap,
                     true,
                  );
                  geometries.push(s.geometry);
               }
            });
         });
         setOpenProviders(init);
         if (geometries.length > 0) flyToGeometries(geometries, engineMap);
      } catch (error)
      {
      } finally
      {
         setLoadingSchemas(false);
      }
   };

   useEffect(() =>
   {
      if (!engineMap || !id) return;
      getData();
   }, [id, engineMap]);

   useEffect(() =>
   {
      // @ts-ignore – thunk
      dispatch(loadBasemaps());
   }, []);

   const handleResetMap = () =>
   {
      dispatch(clearAOIFeatures());
      dispatch(clearAllLayers());
      dispatch(setDrawingAOIMode("NONE"));
      dispatch(setDrawingMode("NONE"));
      dispatch(setMeasureMode("NONE"));
   };

   useEffect(() =>
   {
      handleResetMap();
      return () =>
      {
         handleResetMap();
      };
   }, []);

   const toggleProvider = (key: string) =>
   {
      setOpenProviders((prev) => ({ ...prev, [key]: !prev[key] }));
   };

   // ============== VALIDATE ==============
   const validate = () =>
   {
      const errors: string[] = [];
      const isEmpty = (val: any) =>
         val === undefined || val === null || val === "";

      const getRequiredFields = (step: any) =>
         Object.entries(step.ui?.options || {})
            .filter(([, cfg]: [string, any]) => cfg.required === true)
            .map(([key]) => key);

      for (const step of generalSteps)
      {
         for (const field of getRequiredFields(step))
         {
            if (isEmpty(formData[field])) errors.push(field);
         }
      }

      if (errors.length > 0)
      {
         showError(t("Missing required fields") + ": " + errors.join(", "));
         return false;
      }
      return true;
   };

   // ============== SUBMIT ==============
   const handleSubmit = () =>
   {
      if (!validate()) return;
      toggleConfirm();
   };

   // Only the general-form fields that actually changed vs the loaded order
   const getChangedFields = () =>
   {
      const changed: Record<string, any> = {};
      Object.keys(formData).forEach((key) =>
      {
         if (JSON.stringify(formData[key]) !== JSON.stringify(initialFormData[key]))
         {
            changed[key] = formData[key];
         }
      });
      return changed;
   };

   const handleConfirmSubmit = async () =>
   {
      try
      {
         toggleConfirm();

         const changed = getChangedFields();

         if (Object.keys(changed).length === 0)
         {
            showError(t("No changes to save"));
            return;
         }

         setLoading(true);

         // PATCH: send only the updated fields
         const res = await dispatch(
            patchArchiveOrderAsync({ ...changed, orderId: id }),
         ).unwrap();

         if (
            res?.status === ESttausCode.OK ||
            res?.status === ESttausCode.CREATED
         )
         {
            showSuccess(t("Update successfully"));
            navigate(-1);
         } else
         {
            showError(t(res?.data?.message || res?.message) || t("Update failed"));
         }
      } catch (err: any)
      {
         showError(t(err?.message || getErrorMsg(err)));
      } finally
      {
         setLoading(false);
      }
   };

   return (
      <ContentLayoutManagement
         header={
            <HeaderContent
               breadcrumb={[
                  {
                     title: t("Archive Orders"),
                     path: `/order/management-order${location.search}`,
                  },
                  {
                     title: data?.name,
                     path: `/order/management-order/${data?.id}${location.search}`,
                  },
                  {
                     title: t("Edit"),
                  },
               ]}
            />
         }
      >
         <div className="h-full flex bg-[var(--neutral-1000)] text-white">
            {/* LEFT PANEL */}
            <div className="relative w-5/12 flex flex-col border-r border-[#26324a] pt-6">
               <Spin spinning={loading || loadingSchemas}>
                  <div
                     className="overflow-auto"
                     style={{ height: "calc(100% - 65px)" }}
                  >
                     {/* GENERAL */}
                     <div className="space-y-4">
                        <p className="uppercase text-sm px-6">
                           {t("General Information")}
                        </p>
                        {generalSteps.map((step: any, idx: number) => (
                           <UnifiedDynamicForm
                              key={idx}
                              step={{ ui: step.ui }}
                              formData={formData}
                              setFormData={setFormData}
                              display="grid"
                           />
                        ))}
                     </div>

                     <Divider />

                     {/* PROVIDERS - images (read only) */}
                     {(data?.items || []).map((it: any, idx: number) =>
                     {
                        const stacItems = it?.stac_items || [];
                        if (stacItems.length === 0) return null;
                        const key = it?.provider_code;

                        return (
                           <div key={it?.id ?? idx} className="space-y-4">
                              <div
                                 className="flex justify-between items-center cursor-pointer px-6"
                                 onClick={() => toggleProvider(key)}
                              >
                                 <p className="uppercase text-sm">
                                    {it?.provider?.name || key} ({stacItems.length})
                                 </p>
                                 <span>{openProviders[key] ? "▲" : "▼"}</span>
                              </div>

                              {openProviders[key] && (
                                 <div className="px-6">
                                    {stacItems.map((image: any) => (
                                       <CartImageOrder
                                          key={image?.id}
                                          item={image}
                                          map={engineMap}
                                          providerCode={key}
                                          handleViewInfo={() => handleViewInfo(image)}
                                       />
                                    ))}
                                 </div>
                              )}

                              {idx < (data?.items?.length ?? 0) - 1 && (
                                 <div className="px-6">
                                    <Divider />
                                 </div>
                              )}
                           </div>
                        );
                     })}
                  </div>

                  {/* FOOTER */}
                  <div
                     className="flex absolute bottom-0 w-full justify-between px-6 py-4"
                     style={{ borderTop: "1px solid var(--divider-1)" }}
                  >
                     <Button
                        className="border border-[var(--primary-4)] !bg-[none] !text-[var(--primary-4)]"
                        style={{ background: "none" }}
                        onClick={() => navigate(-1)}
                        disabled={loading}
                     >
                        {t("Back")}
                     </Button>
                     <Button
                        className="w-[151px] !border-none !bg-[var(--primary-4)] text-[var(--primary-950)]"
                        type="primary"
                        onClick={handleSubmit}
                        disabled={loadingSchemas}
                        loading={loading}
                     >
                        {t("Save request")}
                     </Button>
                  </div>
               </Spin>
            </div>

            {/* RIGHT MAP */}
            <div className="flex-1 w-7/12">
               {selectedBasemap && (
                  <ImagesMapVMRS
                     mapType={mapType}
                     view={mapView}
                     onSetEngineMap={(map: any) =>
                     {
                        if (!map) return;
                        map.loaded()
                           ? setEngineMap(map)
                           : map.once("load", () => setEngineMap(map));
                     }}
                  />
               )}
            </div>

            {shouldRenderConfirm && (
               <CommonComponents.ModalUI
                  title={t("Confirm")}
                  open={openConfirm}
                  toggle={toggleConfirm}
                  handleConfirm={handleConfirmSubmit}
                  loading={loading}
               >
                  <p>{t("Are you sure you want to save this request?")}</p>
               </CommonComponents.ModalUI>
            )}

            {shouldRenderViewInfo && (
               <CommonComponents.ModalUI
                  open={openViewInfo}
                  title={t("Information")}
                  toggle={() => toggleViewInfo()}
                  handleConfirm={() => { }}
                  hiddenFooter={true}
                  width={1120}
               >
                  <ImageInfo data={selectedView} open={openViewInfo} />
               </CommonComponents.ModalUI>
            )}
         </div>
      </ContentLayoutManagement>
   );
}
